'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MHButton } from '../buttons/MHButton';

export interface MHDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

export function MHDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
  contentClassName,
}: MHDialogProps) {
  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', className)}
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Dialog'}
    >
      <button
        aria-label="Close dialog"
        className="absolute inset-0 bg-bg-0/80 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      <div
        className={cn(
          'relative z-10 w-full max-w-md rounded-xl bg-surface-1 p-6 animate-in fade-in zoom-in-95 duration-200',
          contentClassName
        )}
      >
        <div className="absolute right-4 top-4">
          <MHButton variant="ghost" size="icon" aria-label="Close" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </MHButton>
        </div>

        {(title || description) && (
          <div className="mb-4 pr-12">
            {title ? <h2 className="text-[18px] font-semibold text-text-1">{title}</h2> : null}
            {description ? <p className="mt-1 text-[13px] font-medium text-text-3">{description}</p> : null}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}
